import { USER_API_URL } from "../config";

// Types for REMLI API
export interface LocationData {
  latitude: number;
  longitude: number;
  accuracy?: number | null;
  timestamp: number;
  source: 'ipapi' | 'browser';
  // IP and network info
  ip?: string;
  network?: string;
  version?: string;
  // Location details
  country?: string;
  country_name?: string;
  country_code?: string;
  country_code_iso3?: string;
  country_capital?: string;
  country_tld?: string;
  region?: string;
  region_code?: string;
  city?: string;
  postal?: string;
  // Geographic info
  continent_code?: string;
  in_eu?: boolean;
  // Time and timezone
  timezone?: string;
  utc_offset?: string;
  // Demographics and culture
  country_calling_code?: string;
  currency?: string;
  currency_name?: string;
  languages?: string;
  country_area?: number;
  country_population?: number;
  // Network info
  asn?: string;
  org?: string;
}

export interface LoginRequest {
  username: string;
  password: string;
  location?: LocationData;
}

export interface ChallengeAnalysis {
  challenge_type: string;
  description: string;
  reason: string;
  input_field_id?: string;
  two_fa_method?: string;
  confidence: number;
}

export interface LoginResponse {
  session_id?: string;
  message?: string;
  challenge_analysis?: ChallengeAnalysis;
}

export interface ChallengeResponse {
  session_id: string;
  challenge: string;
}

export interface ChallengeAnalysisResponse {
  session_id: string;
  challenge_analysis: ChallengeAnalysis;
}

export interface SessionData {
  session_id: string;
  username: string;
  state: string;
  challenge_analysis?: ChallengeAnalysis;
  has_session_data?: boolean;
  session_data?: any;
}

export interface APIKeyResponse {
  id: number;
  api_key: string;
  name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// REMLI API functions
export async function remliLogin(credentials: LoginRequest): Promise<LoginResponse> {
  const token = localStorage.getItem("authToken");

  try {
    const response = await fetch(`${USER_API_URL}/api/remli/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify(credentials),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || "Login failed");
    }

    return data;
  } catch (error) {
    throw error;
  }
}

export async function submitChallenge(request: ChallengeResponse): Promise<LoginResponse> {
  const token = localStorage.getItem("authToken");

  try {
    const response = await fetch(`${USER_API_URL}/api/remli/challenge-response`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || "Challenge submission failed");
    }

    return data;
  } catch (error) {
    throw error;
  }
}

export async function getChallengeAnalysis(sessionId: string): Promise<ChallengeAnalysisResponse> {
  const token = localStorage.getItem("authToken");

  try {
    const response = await fetch(`${USER_API_URL}/api/remli/challenge-analysis/${sessionId}`, {
      headers: {
        "Authorization": `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || "Failed to get challenge analysis");
    }

    return data;
  } catch (error) {
    throw error;
  }
}

export async function listSessions(): Promise<{ sessions: SessionData[] }> {
  const token = localStorage.getItem("authToken");

  try {
    const response = await fetch(`${USER_API_URL}/api/remli/sessions`, {
      headers: {
        "Authorization": `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || "Failed to list sessions");
    }

    return data;
  } catch (error) {
    throw error;
  }
}

export async function getSession(sessionId: string): Promise<SessionData> {
  const token = localStorage.getItem("authToken");

  try {
    const response = await fetch(`${USER_API_URL}/api/remli/sessions/${sessionId}`, {
      headers: {
        "Authorization": `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || "Failed to get session");
    }

    return data;
  } catch (error) {
    throw error;
  }
}

export async function deleteSession(sessionId: string): Promise<{ message: string }> {
  const token = localStorage.getItem("authToken");

  try {
    const response = await fetch(`${USER_API_URL}/api/remli/sessions/${sessionId}`, {
      method: "DELETE",
      headers: {
        "Authorization": `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || "Failed to delete session");
    }

    return data;
  } catch (error) {
    throw error;
  }
}

// API Key functions
export async function generateNewApiKey(name: string = "LinkedIn Cloud Integration"): Promise<APIKeyResponse> {
  const token = localStorage.getItem("authToken");
  if (!token) {
    throw new Error("Not authenticated");
  }

  const response = await fetch(`${USER_API_URL}/api/devapi/key`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
    },
    body: JSON.stringify({ name }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Failed to generate API key");
  }

  return await response.json();
}

export async function getActiveApiKey(): Promise<APIKeyResponse | null> {
  const token = localStorage.getItem("authToken");
  if (!token) {
    throw new Error("Not authenticated");
  }

  try {
    const response = await fetch(`${USER_API_URL}/api/devapi/key`, {
      headers: {
        "Authorization": `Bearer ${token}`,
      },
    });

    if (response.status === 404) {
      return null; // No active API key found
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to get active API key");
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
}

// Location data functions
export async function getLocationFromIP(): Promise<LocationData | null> {
  try {
    const response = await fetch('https://ipapi.co/json/');
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();

    return {
      latitude: data.latitude,
      longitude: data.longitude,
      accuracy: null, // Not available from IP
      timestamp: Date.now(),
      source: 'ipapi',
      // IP and network info
      ip: data.ip,
      network: data.network,
      version: data.version,
      // Location details
      country: data.country,
      country_name: data.country_name,
      country_code: data.country_code,
      country_code_iso3: data.country_code_iso3,
      country_capital: data.country_capital,
      country_tld: data.country_tld,
      region: data.region,
      region_code: data.region_code,
      city: data.city,
      postal: data.postal,
      // Geographic info
      continent_code: data.continent_code,
      in_eu: data.in_eu,
      // Time and timezone
      timezone: data.timezone,
      utc_offset: data.utc_offset,
      // Demographics and culture
      country_calling_code: data.country_calling_code,
      currency: data.currency,
      currency_name: data.currency_name,
      languages: data.languages,
      country_area: data.country_area,
      country_population: data.country_population,
      // Network info
      asn: data.asn,
      org: data.org
    };
  } catch (error) {
    console.warn('Failed to get location from IP:', error);
    return null;
  }
}

// Test function to verify location API
export async function testLocationAPI(): Promise<void> {
  try {
    console.log('Testing location API...');
    const location = await getLocationFromIP();
    if (location) {
      console.log('Location data retrieved successfully:', {
        city: location.city,
        region: location.region,
        country: location.country,
        postal: location.postal,
        timezone: location.timezone,
        org: location.org,
        ip: location.ip
      });
    } else {
      console.log('Failed to retrieve location data');
    }
  } catch (error) {
    console.error('Error testing location API:', error);
  }
}
